<template>
  <section id="ai-gallery" class="gallery">
    <div ref="stageEl" class="gallery__stage">
      <!-- Ambient wash: the current piece, blurred and darkened, filling the
           stage so the screen is never empty around a narrow image. One element
           with a swapped src rather than a stack of layers — seventeen
           full-screen images would all be decoded and composited for a
           backdrop nobody looks at directly. It only re-rasterises when the
           index changes, not while the rail is moving. -->
      <img v-if="active" :src="thumb(active.image)" alt="" class="gallery__wash">
      <div class="gallery__veil"/>

      <!-- The rail. Every piece sits at its true aspect ratio, sized by height
           alone so width follows the image, and the whole row slides so the
           current one is centred. Neighbours are simply the frames either
           side, which is what makes the next one visible without a separate
           preview component. -->
      <div ref="railEl" class="rail" :style="{ '--lead-ratio': leadRatio }">
        <!-- Skeleton frames, at the shapes the pictures will take. Drawing the
             compiled-in row first and swapping was the drift: a picture sat in
             the frame, the live row arrived, and it was pushed out from under
             the reader. Nothing real is drawn until it can all be drawn. -->
        <figure
          v-for="(piece, index) in rail"
          v-show="ready"
          :key="piece.id"
          class="frame"
          :class="{ 'is-active': index === activeIndex }"
          :style="{ '--ratio': ratioOf(piece) }"
        >
          <img
            :src="mid(piece.image)"
            :alt="piece.title"
            :width="piece.width"
            :height="piece.height"
            decoding="async"
            class="frame__image"
            @load="onShot"
            @error="onShot"
          >

          <!-- On the picture, the way the wall shows it. Only the centred one:
               a count under every frame in the row would read as a scoreboard
               rather than as a fact about the piece being looked at. -->
          <figcaption v-if="index === activeIndex" class="frame__meta">
            <ShareButton
              :id="piece.id"
              kind="piece"
              :title="piece.title"
            />

            <CountBadge v-if="activeViews" :count="activeViews" kind="view" />
          </figcaption>
        </figure>

        <figure
          v-for="piece in ready ? [] : rail"
          :key="`sk-${piece.id}`"
          class="frame is-skeleton"
          :style="{ '--ratio': ratioOf(piece) }"
          aria-hidden="true"
        >
          <span class="frame__sk skeleton" />
        </figure>
      </div>

      <!-- Caption sits under the rail rather than over the image: with ratios
           this varied there is no corner that is reliably empty. -->
      <footer class="gallery__foot">
        <div class="caption">
          <div class="caption__text">
            <h3 class="caption__title">{{ active?.title }}</h3>
          </div>

          <!-- Kept as one unit. Split across the two ends of the row, the total
               read as a stray "/ 17" with nothing in front of it. -->
          <span class="caption__count">
            <span class="caption__index">{{ pad(activeIndex + 1) }}</span>
            <span class="caption__total">/ {{ pad(rail.length) }}</span>
          </span>
        </div>

        <div class="progress" aria-hidden="true">
          <span
            v-for="(piece, index) in rail"
            :key="`tick-${piece.id}`"
            class="progress__tick"
            :class="{ 'is-active': index === activeIndex }"
          />
        </div>
      </footer>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { gsap } from 'gsap';
import { aiGenerations } from '~/data/content';
import { orderFeatured, preloadImages } from '~/composables/useFeatured';

const stageEl = ref(null);
const railEl = ref(null);

// Where the rail was left. The offset is what a phone needs to scroll back to,
// and the index is what everything else needs to agree about.
const RAIL_SCROLL_KEY = 'h-scroll-rail-scroll';
const RAIL_INDEX_KEY = 'h-scroll-rail-i';

// Which picture the caption, the counter and the ticks open on, read before the
// first client render rather than corrected after it.
//
// A component's mount is too late for this. The script at the close of the body
// has already put the right frame, tick and title in the page, and Vue hydrates
// against what its own render says: rendering the first picture here and fixing
// it in onMounted would have Vue overwrite all of that back to picture one on
// the way in, and the reader would watch it walk across a second time.
//
// The server has no reader to have left anything, so it renders the first, and
// the mismatch this creates on the client is the point: it patches to what the
// blocking script already painted.
function initialIndex() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = sessionStorage.getItem(`${RAIL_INDEX_KEY}:${window.location.pathname}`);
    const i = raw === null ? NaN : parseInt(raw, 10);
    if (!Number.isFinite(i) || i <= 0) return 0;
    // Against the row the server rendered, which is the one on screen right
    // now. A visit that ended on the seventeenth picture and came back to a
    // shorter row would otherwise name a piece that is not there, and `active`
    // is what the wash and the caption are both read from: undefined empties
    // the stage rather than opening it late.
    return Math.min(i, Math.max(aiGenerations.length - 1, 0));
  } catch {
    return 0;
  }
}

const activeIndex = ref(initialIndex());

// Below this the rail is a plain horizontal scroller that the reader swipes,
// rather than something the page's scroll position drives.
//
// It has to be, because the thing that drives it does not exist here: the rail
// is moved by a dwell folded into the horizontal track's pin, and the pin is
// torn down below 768px. Left as it was, the panel had no height, the stage
// collapsed to zero, and the page was blank with all seventeen pictures loaded
// into a box nobody could see.
const MOBILE = '(max-width: 768px)';
const isSmall = () => typeof window !== 'undefined' && window.matchMedia(MOBILE).matches;

// What the rail carries: the pieces marked for it, and nothing else. Uploading
// is not choosing, so a new picture reaches the wall and stays off the rail
// until somebody says otherwise.
//
// One order, shared with the strip on the home page. They had drifted: a newly
// featured picture led there and came eighteenth here, which reads as it not
// having reached Explore at all.
const pieces = ref(aiGenerations);
const rail = computed(() => orderFeatured(pieces.value, aiGenerations));

const active = computed(() => rail.value[activeIndex.value] ?? null);

// Half the first frame's width, expressed as a ratio the stylesheet can
// multiply by the frame height. It shifts the rail left so the opening piece is
// centred with no transform at all, which is the position the very first paint
// has to show — before any script has run to place it.
const leadRatio = computed(() => {
  const first = rail.value[0];
  return first?.width && first?.height ? first.width / first.height : 0;
});
// A piece's own proportions, handed to the stylesheet so a frame can be given
// both of its limits and take whichever runs out first.
const ratioOf = (piece) => (piece?.width || 1) / (piece?.height || 1);

const thumb = (src) => (src || '').replace('/images/ai/', '/images/ai/thumb/');
const mid = (src) => (src || '').replace('/images/ai/', '/images/ai/mid/');
const pad = (n) => String(n).padStart(2, '0');

// Distance from the rail's origin to each frame's centre — exactly how far the
// rail must travel to bring that frame to the middle. No stage-width term:
// the rail is anchored at left: 50%, so its origin already sits at the centre
// and subtracting half the stage would count that offset twice.
//
// Measured rather than computed, because frames are sized by their own aspect
// ratio and their widths are only known once the images have decoded.
let offsets = [];
let setX = null;
let measureRaf = null;

// What the dwell last reported, so a re-measure re-applies the position the
// reader is actually on rather than the rounded index the caption shows.
let lastPosition = null;

function savedRailX() {
  try {
    const raw = sessionStorage.getItem(`h-scroll-rail-x:${window.location.pathname}`);
    if (raw === null) return null;
    const x = parseFloat(raw);
    return Number.isFinite(x) ? x : null;
  } catch {
    return null;
  }
}

// The inverse of apply(): which continuous position leaves the rail at this
// offset. Frames are all different widths, so this walks them rather than
// dividing by an average.
// The transform above is a desktop answer: below the breakpoint the rail is a
// native horizontal scroller and there is no transform to put back, only a
// scroll offset, which nothing was keeping. Every other long thing on the site
// resumes where it was left and this was the one that did not.
// A swipe, a trackpad, or the keyboard. Nothing here is something the page can
// do to itself.
const RAIL_INPUT = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

function railScrollKey() {
  return `${RAIL_SCROLL_KEY}:${window.location.pathname}`;
}

function savedRailScroll() {
  try {
    const raw = sessionStorage.getItem(railScrollKey());
    if (raw === null) return null;
    const v = parseFloat(raw);
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

let railScrollRestored = false;

// Keyed off input, never off 'scroll'. The same rule the blocking script keeps
// for the page's own position, and for the same reason: the rail is scrolled by
// plenty of things that are not the reader. Putting it back raises an event.
// Swapping the skeletons for the pictures raises one, and a mandatory snap
// container re-snaps when its children change. Any of those, written down as
// though somebody had swiped, replaced a good position with the start of the
// row, and the next reload had nothing to go back to.
let railArmed = false;

function armRailScroll() {
  railArmed = true;
}

// Once, and not until the row is wide enough to hold the offset. The skeletons
// are at the shapes the pictures will take, so that is true well before any
// picture has decoded and the rail is put back on the frame the reader left
// rather than sliding to it once the images land.
function restoreRailScroll() {
  const rail = railEl.value;
  if (railScrollRestored || !rail) return;

  // Already placed, and this is the ordinary case on a full page load: the
  // script at the close of the body puts the rail back before anything is
  // painted, which is far earlier than a component's mount can be. Anything
  // other than the start of the row means somebody has already decided where
  // it should be, whether that was the script or a reader who swiped while the
  // bundle was still arriving. Neither wants moving.
  if (rail.scrollLeft > 0) {
    railScrollRestored = true;
    return;
  }

  const saved = savedRailScroll();
  if (saved === null) {
    railScrollRestored = true;
    return;
  }

  if (rail.scrollWidth - rail.clientWidth < saved) return;

  rail.scrollLeft = saved;
  railScrollRestored = true;
}

function positionForX(x) {
  const last = offsets.length - 1;
  if (last < 1) return 0;

  const target = (offsets[0] ?? 0) - x;
  for (let i = 0; i < last; i += 1) {
    const a = offsets[i];
    const b = offsets[i + 1];
    if (target <= b) return b > a ? i + (target - a) / (b - a) : i;
  }
  return last;
}

function measure() {
  const rail = railEl.value;
  const stage = stageEl.value;
  if (!rail || !stage) return;

  // Only the frames that are laid out. Two rows are in the rail at once: the
  // real one, rendered from the first paint and hidden with v-show until the
  // pictures have decoded, and the skeletons standing in for it. A query for
  // .frame answers with both, so this was seventeen boxes of no size followed
  // by seventeen real ones.
  //
  // That list broke the restore below in two ways at once. offsets[0] came back
  // 0 instead of the first frame's centre, which is the origin every offset is
  // measured from, and the saved position then resolved to an index past the
  // end of a row half its apparent length. Reloading deep in the gallery landed
  // on the last picture, and the wash and the caption went blank on the way
  // there because the index was outside the rail entirely.
  offsets = Array.from(rail.querySelectorAll('.frame'))
    .filter((frame) => frame.offsetWidth > 0)
    .map((frame) => frame.offsetLeft + frame.offsetWidth / 2);

  // On a phone the browser owns the rail's scroll position. Writing a transform
  // over the top of it would drag the picture back as the reader swiped, so the
  // position is put back by scrolling it rather than by placing it.
  if (isSmall()) {
    restoreRailScroll();

    // Which picture that leaves in the middle. On a phone nothing else works
    // this out: the index arrives with a scroll event, and the rail is put back
    // by the script at the close of the body, before this component exists, so
    // no such event ever reaches the listener below.
    //
    // Left at zero it is not only the counter and the ticks that are wrong. The
    // centred frame is the one that carries is-active, and without it the
    // picture the reader came back to sits at the same weight as its
    // neighbours: restored, and looking like it was not.
    const k = centredIndex();
    if (k >= 0 && k !== activeIndex.value) activeIndex.value = k;
    return;
  }

  // Before the dwell has reported anything, the position comes from wherever
  // the last visit left it. Applying the first frame instead is what made a
  // reload deep in the gallery open on picture one and then jump: this runs on
  // mount, well before the scroll position is restored, and its transform
  // overrode the one the blocking script had painted with.
  if (lastPosition === null && offsets.length > 1) {
    const x = savedRailX();
    if (x !== null && x < 0) {
      lastPosition = positionForX(x);
      activeIndex.value = Math.round(lastPosition);
    }
  }

  apply(lastPosition ?? 0);
}

function scheduleMeasure() {
  if (measureRaf) cancelAnimationFrame(measureRaf);
  measureRaf = requestAnimationFrame(() => {
    measureRaf = null;
    measure();
  });
}

// Each image changes the rail's geometry as it arrives, so re-measure per load
// rather than waiting on all of them — a single slow image would otherwise
// leave the rail mispositioned until it finished.
// False until the pictures that will be on screen are decoded and the rail can
// appear whole. Until then it is skeleton frames at the same shapes, which is
// the only way the row never moves under anybody.
const ready = ref(false);

function onShot() {
  scheduleMeasure();
}

// Position is continuous, not stepped. The hold reports a fraction, and the
// rail is placed between the two frames it falls between, so the row glides
// under the pointer instead of jumping a whole image at a time.
function apply(position) {
  if (!offsets.length || !setX || isSmall()) return;

  const last = offsets.length - 1;
  const clamped = Math.min(Math.max(position, 0), last);
  const i = Math.floor(clamped);
  const f = clamped - i;
  const from = offsets[i] ?? 0;
  const to = offsets[Math.min(i + 1, last)] ?? from;

  // Relative to the first frame, whose centring the stylesheet already carries.
  // Absolute offsets would have meant a non-zero transform at index 0, so the
  // rail painted uncentred and only jumped into place once this first ran.
  setX(-(from + (to - from) * f - (offsets[0] ?? 0)));

  // The rail now carries its own inline transform, which outranks the rule the
  // blocking script wrote to hold this position through the first paint. Not
  // released any earlier: until this line has run once there is nothing else
  // placing the rail, and dropping the rule would send it back to the start.
  releasePreset();
}

// The rule in the head that lights the right tick through the first paint, and
// on a phone the right frame with it. Dropped once this component has hydrated,
// because by then activeIndex carries the same answer and the classes it writes
// are the thing that has to win when the reader swipes.
function releaseIndexPreset() {
  if (typeof document === 'undefined') return;
  document.getElementById('hscroll-index-preset')?.remove();
}

let presetReleased = false;

function releasePreset() {
  if (presetReleased || typeof document === 'undefined') return;
  presetReleased = true;

  // Held until the patch lands. The rule also carries which frame is lit, and
  // the index that decides it has only just been written to a ref: drop the
  // rule in the same tick and there is a frame where neither says so.
  nextTick(() => {
    document.getElementById('hscroll-rail-preset')?.remove();
  });
}

const route = useRoute();
const scrollState = useState('horizontalScroll');

// Each picture keeps its own count, so what is recorded is whichever one is
// centred, once it has been centred long enough to have been looked at.
useDwellView('piece', () => active.value?.id);

const { load, useCounts } = useLiveContent();
const counts = useCounts();

// Absent until the server decides it is worth showing, so there is nothing to
// render for a picture nobody has found yet.
const activeViews = computed(() => Number(counts.value?.piece?.[active.value?.id]) || 0);

// A share made from here means "look at this piece", so the link points back at
// this page, which opens on it. The wall builds its own link to itself.


useScrollHold('ai-gallery', {
  // Roughly two thirds of a screen per piece. A full screen each meant this one
  // panel swallowed sixteen screens of scrolling.
  length: () => Math.max(rail.value.length - 1, 0) * window.innerHeight * 0.62,
  onProgress: (progress) => {
    // The pin is built before the page is scrolled back, so its first report
    // describes the top of the track rather than where the reader was. Acting
    // on it sent the rail to the first picture for a few frames, between the
    // position the blocking script painted and the one the restore lands on.
    // Only ignored when there is a restored position to protect: with nothing
    // saved, the first report is the truth.
    if (scrollState.value?.restoring && lastPosition !== null) return;

    const position = progress * Math.max(rail.value.length - 1, 0);
    lastPosition = position;
    apply(position);

    const next = Math.round(position);
    if (next !== activeIndex.value) activeIndex.value = next;
  },
});

// Which piece is centred in the native scroller, so the caption, the counter
// and the ticks keep up with a swipe.
let railScrollRaf = null;

// offsets are frame centres in rail coordinates; the centre of what is on
// screen is the scroll position plus half the visible width.
function centredIndex() {
  const rail = railEl.value;
  if (!rail || !offsets.length) return -1;

  const middle = rail.scrollLeft + rail.clientWidth / 2;
  let nearest = 0;
  let best = Infinity;
  offsets.forEach((centre, i) => {
    const d = Math.abs(centre - middle);
    if (d < best) {
      best = d;
      nearest = i;
    }
  });
  return nearest;
}

function onRailScroll() {
  if (railScrollRaf) return;
  railScrollRaf = requestAnimationFrame(() => {
    railScrollRaf = null;
    const rail = railEl.value;
    if (!rail || !offsets.length) return;

    const nearest = centredIndex();
    if (nearest >= 0 && nearest !== activeIndex.value) activeIndex.value = nearest;

    // Only once somebody has actually taken hold of the row, and never before
    // the restore has landed.
    if (railArmed && railScrollRestored) {
      try {
        sessionStorage.setItem(railScrollKey(), String(Math.round(rail.scrollLeft)));
        // The same key the desktop writes, which only records one while there
        // is a pin to record it from. Below the breakpoint there is none, and
        // without this the next load had an offset to scroll to and nothing to
        // say which picture that was.
        sessionStorage.setItem(
          `${RAIL_INDEX_KEY}:${window.location.pathname}`,
          String(nearest)
        );
      } catch {
        // private mode / storage disabled
      }
    }
  });
}

function handleResize() {
  scheduleMeasure();
}

// Opening on a specific piece, when arriving from the Recent generations strip.
//
// The dwell is part of the master timeline, so landing on a piece means
// scrolling to a point *inside* that dwell — scrollToHold does the arithmetic.
// It only exists once the page has built the pin, which happens after this
// component mounts, so this waits for it to appear rather than assuming it.
function openRequestedPiece() {
  const wanted = route.query.piece;
  if (!wanted) return;

  const index = rail.value.findIndex((piece) => piece.id === wanted);
  if (index < 0) return;

  const stop = watch(
    () => scrollState.value?.scrollToHold,
    (jump) => {
      if (!jump) return;

      // Two frames on: the pin has just been built and refreshed, and the hold
      // positions are only final after that settles.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          activeIndex.value = index;
          lastPosition = index;
          apply(index);
          jump('ai-gallery', index / Math.max(rail.value.length - 1, 1), { immediate: true });

          // The query has done its job. Cleared the same way hashes are, so a
          // shared or bookmarked URL is not pinned to one image forever.
          if (window.location.search) {
            window.history.replaceState(window.history.state, '', window.location.pathname);
          }
        });
      });

      stop();
    },
    { immediate: true }
  );
}

onMounted(async () => {
  setX = gsap.quickSetter(railEl.value, 'x', 'px');
  measure();
  // After measure, which is where the index is settled on both breakpoints, and
  // a tick later so the class it caused is on the element before the rule
  // holding the same position is taken away.
  nextTick(releaseIndexPreset);
  openRequestedPiece();
  window.addEventListener('resize', handleResize);
  railEl.value?.addEventListener('scroll', onRailScroll, { passive: true });
  RAIL_INPUT.forEach((type) => {
    railEl.value?.addEventListener(type, armRailScroll, { passive: true });
  });

  // After the built-in row is already on screen, so the panel is never empty
  // while this is in flight. A different set of pictures is a different set of
  // widths, so the offsets have to be taken again and the dwell rebuilt around
  // however many there now are.
  try {
    const live = await load('generations', aiGenerations);
    pieces.value = live;

    // Only the ones that will be on screen. The rest of the row loads behind
    // them, which is what the frames beyond the fold were always doing.
    await preloadImages(
      orderFeatured(live, aiGenerations).slice(0, 4).map((piece) => mid(piece.image))
    );
  } finally {
    // Whatever happened, the skeletons do not get to stay.
    ready.value = true;
  }

  await nextTick();
  scheduleMeasure();
  scrollState.value.rebuild?.();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  railEl.value?.removeEventListener('scroll', onRailScroll);
  RAIL_INPUT.forEach((type) => {
    railEl.value?.removeEventListener(type, armRailScroll);
  });
  if (measureRaf) cancelAnimationFrame(measureRaf);
  if (railScrollRaf) cancelAnimationFrame(railScrollRaf);
  setX = null;
});
</script>

<style lang="scss" scoped>
.gallery {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.gallery__stage {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  // Nothing inside can paint outside it, which lets the browser keep repaints
  // of the page behind from reaching in here.
  contain: paint;
  background: #0b0f14;
  // Shared, because the rail's resting offset is half the opening frame's
  // width and that is only derivable from the frame's own height.
  --frame-h: clamp(38vh, 56vh, 62vh);
  // The other limit. Height alone decides a frame's size until the picture is
  // wide enough that the width is what runs out first, and then this does.
  // Generous here so the neighbours either side are still in view; a phone
  // spends nearly all of it, having no neighbours to show.
  --frame-max-w: calc(100vw - 8rem);
}

.gallery__wash {
  position: absolute;
  // Blurred small and scaled up rather than blurred across the whole screen.
  //
  // A 34px blur over a phone viewport is on the order of thirty million texel
  // reads each time it rasterises, and it rasterises on any repaint that
  // reaches it. Changing theme animates the root's background colour for
  // 300ms, which is about eighteen such repaints back to back: that is what
  // was freezing the toggle here and nowhere else.
  //
  // An eighth the box at an eighth the radius is visually identical once
  // scaled back, and rasterises a sixty-fourth of the area. The offsets keep
  // the same 15% bleed the old centred scale(1.15) gave, so the blur still has
  // no visible edge: -7.5% to 107.5% on both axes.
  top: -7.5%;
  left: -7.5%;
  width: 12.5%;
  height: 12.5%;
  object-fit: cover;
  transform-origin: 0 0;
  transform: scale(9.2);
  filter: blur(3.7px) saturate(1.15);
  opacity: 0.5;
  // Its own layer, so a repaint anywhere else does not touch the blur at all.
  will-change: transform;
}

.gallery__veil {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 50% 45%, rgba(11, 15, 20, 0.15), rgba(11, 15, 20, 0.82) 78%),
    linear-gradient(to top, rgba(11, 15, 20, 0.92) 0%, rgba(11, 15, 20, 0) 45%);
}

.rail {
  position: absolute;
  // Anchored at the horizontal centre, so a frame is centred when the rail has
  // moved by exactly that frame's own offset — no stage-width term at runtime.
  top: 0;
  left: 50%;
  // ...minus half the opening frame, so that one starts centred under no
  // transform. Without this the rail's resting place was the first frame's
  // left edge, and every load painted there before sliding into position.
  // Capped the same way a frame is, or a wide opening picture would be shifted
  // by more than its own width and open off-centre before any script runs.
  margin-left: calc(min(var(--frame-h) * var(--lead-ratio, 0), var(--frame-max-w)) * -0.5);
  height: 100%;
  display: flex;
  align-items: center;
  gap: clamp(1rem, 2.5vw, 2.75rem);
  will-change: transform;
}

.frame {
  position: relative;
  flex: 0 0 auto;
  // The frame is the picture's own shape and takes whichever of its two limits
  // runs out first. A tall limit alone keeps a portrait portrait and a wide one
  // wide but says nothing about width, and a landscape piece at full frame
  // height is wider than a phone: it hangs off both edges, taking the share and
  // view counts anchored to its bottom corner with it.
  aspect-ratio: var(--ratio, 1);
  height: min(var(--frame-h), calc(var(--frame-max-w) / var(--ratio, 1)));
  width: auto;
  margin: 0;
  border-radius: 10px;
  overflow: hidden;
  opacity: 0.32;
  transform: scale(0.88);
  transition: opacity 0.45s ease, transform 0.45s ease;
  // One large shadow, not one per frame. A 60px shadow on every frame in the
  // rail is a large blurred surface each, and all but the centred one sit at
  // 0.32 opacity behind the picture being looked at.
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);

  &.is-active {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  }
}

.frame__meta {
  position: absolute;
  inset: auto 0 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.8rem;
  padding: 1.6rem 0.85rem 0.6rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.9);
  // Enough of a wash to stay legible over a pale picture, fading out before it
  // starts covering the work.
  background: linear-gradient(to top, rgba(11, 15, 20, 0.72) 0%, rgba(11, 15, 20, 0) 100%);
  // The strip is not a target; only the button inside it is.
  pointer-events: none;
}


.frame__sk {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

// A stand-in frame is the shape its picture will be, so the row is its final
// width before any of it has arrived.
.frame.is-skeleton {
  opacity: 1;
  transform: none;
}

.frame__image {
  display: block;
  width: 100%;
  height: 100%;
  // Belt and braces: the frame is already at the picture's own ratio, so this
  // crops nothing. It only stops a stray pixel of letterboxing at the rounded
  // edge.
  object-fit: cover;
}

.gallery__foot {
  position: absolute;
  inset: auto clamp(1.25rem, 4vw, 3rem) clamp(1.25rem, 4vh, 2.5rem);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.caption {
  display: flex;
  align-items: baseline;
  gap: clamp(0.75rem, 1.5vw, 1.25rem);
}

.caption__count {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-shrink: 0;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
}

.caption__index {
  color: rgba(255, 255, 255, 0.9);
  // Fixed width so the title beside it does not shift when the count goes from
  // one digit to two.
  font-variant-numeric: tabular-nums;
}

.caption__total {
  color: rgba(255, 255, 255, 0.45);
  font-variant-numeric: tabular-nums;
}

.caption__text {
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  min-width: 0;
  // Takes the slack so the count stays pinned to the right as titles change
  // length.
  flex: 1;
}

.caption__title {
  margin: 0;
  font-size: clamp(0.95rem, 0.6vw + 0.8vh, 1.35rem);
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


.progress {
  display: flex;
  gap: 4px;
}

.progress__tick {
  height: 2px;
  flex: 1;
  background: rgba(255, 255, 255, 0.18);
  transition: background 0.3s ease;

  &.is-active {
    background: rgba(255, 255, 255, 0.85);
  }
}

@media (max-width: 768px) {
  .gallery__stage {
    --frame-h: clamp(30vh, 42vh, 46vh);
    // Nearly the whole screen. There are no neighbours to leave room for here,
    // only the swipe, and a wide piece needs every pixel of it to be shown
    // whole rather than hung off both edges.
    --frame-max-w: calc(100vw - 2.5rem);
  }

  .frame {
    // Each picture parks itself in the middle when the swipe stops.
    scroll-snap-align: center;
  }

  // The stage takes its height from the screen rather than from a pinned
  // ancestor, because on a phone there is no pinned ancestor: the track is
  // torn down and every panel is `height: auto`, so `height: 100%` here
  // resolved against nothing and collapsed the whole page to zero.
  //
  // svh, never dvh. dvh tracks the address bar and would re-lay the gallery out
  // on every frame of it moving, which is the fault that was just fixed in the
  // hero.
  .gallery,
  .gallery__stage {
    height: 100vh;
    height: 100svh;
  }

  // Back into the flow and handed to the browser. `left` and the transform both
  // have to go: the transform is written by GSAP for the desktop dwell, and
  // left over here it would offset the native scroller by half the screen.
  .rail {
    position: relative;
    left: 0;
    margin-left: 0;
    transform: none !important;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    // So the first and last pictures can reach the middle of the screen.
    padding-inline: 50vw;

    &::-webkit-scrollbar { display: none; }
  }

  // Every picture is legible while swiping, rather than only the centred one.
  .frame {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .frame {
    transition: none;
  }
}
</style>
